import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Timer, Play, Pause, RotateCcw, Target, Trophy } from 'lucide-react';

const DURATIONS = [5, 10, 15, 20];
const GOALS = [100, 250, 500];

export const SprintTimer = ({ wordCount = 0 }) => {
  const [minutes, setMinutes] = useState(15);
  const [goal, setGoal] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(15 * 60);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const intervalRef = useRef(null);
  const startWordsRef = useRef(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!running) return undefined;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          setFinished(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const selectDuration = (m) => {
    setMinutes(m);
    setSecondsLeft(m * 60);
    setRunning(false);
    setFinished(false);
    startedRef.current = false;
  };

  const handleStartPause = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      startWordsRef.current = wordCount;
    }
    setRunning((r) => !r);
  };

  const reset = () => selectDuration(minutes);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const progress = 1 - secondsLeft / (minutes * 60);
  const started = running || secondsLeft !== minutes * 60;
  const written = startedRef.current ? Math.max(0, wordCount - startWordsRef.current) : 0;
  const goalMet = goal !== null && started && written >= goal;

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        goalMet ? 'bg-emerald-50 border-emerald-200' : finished ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
      }`}
      data-testid="sprint-timer"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${goalMet ? 'bg-emerald-100' : finished ? 'bg-rose-100' : 'bg-amber-100'}`}>
            {goalMet ? <Trophy className="h-4 w-4 text-emerald-600" /> : <Timer className={`h-4 w-4 ${finished ? 'text-rose-600' : 'text-amber-800'}`} />}
          </div>
          <div>
            <p className="text-sm font-medium text-amber-900">Writing Sprint</p>
            <p
              className={`font-display text-2xl font-bold tabular-nums ${
                finished ? 'text-rose-600' : secondsLeft <= 60 && started ? 'text-rose-600' : 'text-amber-950'
              }`}
              data-testid="sprint-timer-display"
            >
              {finished ? "Time's up!" : `${mm}:${ss}`}
            </p>
          </div>
          {goal !== null && started && (
            <div className={`ml-2 px-3 py-1.5 rounded-full text-sm font-medium tabular-nums ${goalMet ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-amber-200 text-amber-800'}`} data-testid="sprint-goal-progress">
              {Math.min(written, goal)} / {goal} words
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {!started && !finished && (
            <>
              <div className="flex items-center space-x-1" data-testid="sprint-duration-options">
                {DURATIONS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => selectDuration(m)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      minutes === m
                        ? 'bg-amber-900 text-amber-50'
                        : 'bg-white border border-amber-200 text-amber-800 hover:bg-amber-100'
                    }`}
                    data-testid={`sprint-duration-${m}`}
                  >
                    {m}m
                  </button>
                ))}
              </div>
              <span className="text-amber-300">|</span>
              <div className="flex items-center space-x-1" data-testid="sprint-goal-options">
                <Target className="h-3.5 w-3.5 text-amber-700" />
                {GOALS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGoal(goal === g ? null : g)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      goal === g
                        ? 'bg-emerald-700 text-emerald-50'
                        : 'bg-white border border-amber-200 text-amber-800 hover:bg-amber-100'
                    }`}
                    data-testid={`sprint-goal-${g}`}
                  >
                    {g}w
                  </button>
                ))}
              </div>
            </>
          )}
          {!finished && (
            <Button
              size="sm"
              onClick={handleStartPause}
              className="bg-amber-900 hover:bg-amber-800 text-amber-50"
              data-testid="sprint-start-pause-button"
            >
              {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          )}
          {(started || finished) && (
            <Button
              size="sm"
              variant="outline"
              onClick={reset}
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="sprint-reset-button"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {started && !finished && (
        <div className="mt-3 h-1.5 rounded-full bg-amber-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-amber-700 transition-[width] duration-1000 ease-linear"
            style={{ width: `${progress * 100}%` }}
            data-testid="sprint-progress-bar"
          />
        </div>
      )}
      {goalMet && !finished && (
        <p className="mt-2 text-sm text-emerald-700" data-testid="sprint-goal-met-message">
          Goal smashed with time to spare — keep the momentum going!
        </p>
      )}
      {finished && (
        <p className={`mt-2 text-sm ${goalMet ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="sprint-finished-message">
          {goalMet
            ? `Pens down — you wrote ${written} words and hit your goal!`
            : 'Pens down! Give it a title and save your story.'}
        </p>
      )}
    </div>
  );
};
